/**
 * The ONE canonical registry for index / universe ticker lists.
 *
 * Before this file the same logical list ("S&P 1500") was keyed four different
 * ways — the DB `index_membership` slug (`sp_1500`), the Seasonality CSV key
 * (`sp1500`), the breadth code (`SP15`), and the CSV filename (`sp1500.csv`) —
 * with no reconciliation, so the stores silently drifted. Everything now funnels
 * through `canonicalizeSlug()` into a single canonical slug, and every consumer
 * reads the one `index_membership` store keyed by it.
 *
 * The canonical slugs are the ones already stored in the DB (so no data
 * migration is needed); we only ADD `sp400` / `sp600` (never had a DB home) and
 * fold every historical alias into `canonicalizeSlug`. The committed seed CSVs in
 * `data/universe/` are named by canonical slug (`sp_1500.csv`, …).
 */

/** The canonical index slugs — exactly the keys `index_membership` stores. */
export type IndexSlug =
  | "sp500"
  | "sp400"
  | "sp600"
  | "sp_1500"
  | "russell_1000"
  | "russell_2000"
  | "nasdaq_100"
  | "dow_jones"
  | "nyse"
  | "nasdaq"
  | "amex"
  | "base"
  | "mega_cap"
  | "large_cap"
  | "mid_cap"
  | "small_cap"
  | "micro_cap"
  | "etfs";

export const INDEX_SLUGS: readonly IndexSlug[] = [
  "sp500",
  "sp400",
  "sp600",
  "sp_1500",
  "russell_1000",
  "russell_2000",
  "nasdaq_100",
  "dow_jones",
  "nyse",
  "nasdaq",
  "amex",
  "base",
  "mega_cap",
  "large_cap",
  "mid_cap",
  "small_cap",
  "micro_cap",
  "etfs",
] as const;

/**
 * Manual lists — user-uploaded, with NO FMP source. The FMP auto-refresh
 * (nightly + "Refresh now") skips these so it never wipes what you uploaded.
 * None since 2026-11-01: `base` is built by rule (lib/indices/screenerRules.ts).
 * The list it replaced is data/universe/base-hand-kept-2026-11-01.csv; putting
 * "base" back here and uploading that file is the way back.
 */
export const MANUAL_SLUGS: ReadonlySet<IndexSlug> = new Set<IndexSlug>();

/**
 * The lists whose every symbol the nightly seasonality deepen takes to IPO
 * history and the Explorer's index is baked over. `etfs` is not one: about
 * 4,000 full histories for a list the Explorer already offers by its own ETF
 * group.
 */
export const SEASONALITY_SLUGS: readonly IndexSlug[] = INDEX_SLUGS.filter((s) => s !== "etfs");

/** Display label per slug (pane titles, pickers, upload UI). */
export const INDEX_LABEL: Record<IndexSlug, string> = {
  sp500: "S&P 500",
  sp400: "S&P 400",
  sp600: "S&P 600",
  sp_1500: "S&P 1500",
  russell_1000: "Russell 1000",
  russell_2000: "Russell 2000",
  nasdaq_100: "NASDAQ 100",
  dow_jones: "Dow Jones",
  nyse: "NYSE Stocks",
  nasdaq: "Nasdaq Stocks",
  amex: "AMEX Stocks",
  base: "Base",
  mega_cap: "Mega Cap",
  large_cap: "Large Cap",
  mid_cap: "Mid Cap",
  small_cap: "Small Cap",
  micro_cap: "Micro Cap",
  etfs: "ETFs",
};

/** Committed seed file per slug, under `data/universe/` (one bare ticker/line). */
export const INDEX_SEED_FILE: Record<IndexSlug, string> = {
  sp500: "sp500.csv",
  sp400: "sp400.csv",
  sp600: "sp600.csv",
  sp_1500: "sp_1500.csv",
  russell_1000: "russell_1000.csv",
  russell_2000: "russell_2000.csv",
  nasdaq_100: "nasdaq_100.csv",
  dow_jones: "dow_jones.csv",
  nyse: "nyse.csv",
  nasdaq: "nasdaq.csv",
  amex: "amex.csv",
  base: "base.csv",
  mega_cap: "mega_cap.csv",
  large_cap: "large_cap.csv",
  mid_cap: "mid_cap.csv",
  small_cap: "small_cap.csv",
  micro_cap: "micro_cap.csv",
  etfs: "etfs.csv",
};

/**
 * Every historical alias → canonical slug. Folds in the four naming schemes:
 * DB legacy (`sp1500`, `ru2000`), Seasonality CSV keys (`ru1000`, `nq100`, …),
 * breadth codes (`SP15`, `R1K`, `NDX`, `DJ`, `NY`, `NQ`, `SP`), and loose spellings.
 * Keys are lowercased before lookup, so breadth codes match case-insensitively.
 */
const ALIASES: Record<string, IndexSlug> = {
  // S&P 500 / 400 / 600
  sp500: "sp500", sp_500: "sp500", sp: "sp500",
  sp400: "sp400", sp_400: "sp400",
  sp600: "sp600", sp_600: "sp600",
  // S&P 1500
  sp_1500: "sp_1500", sp1500: "sp_1500", sp15: "sp_1500",
  // Russell 1000 / 2000
  russell_1000: "russell_1000", russell1000: "russell_1000", ru1000: "russell_1000", r1k: "russell_1000",
  russell_2000: "russell_2000", russell2000: "russell_2000", ru2000: "russell_2000", r2k: "russell_2000",
  // NASDAQ 100 + Composite
  nasdaq_100: "nasdaq_100", nasdaq100: "nasdaq_100", nas100: "nasdaq_100", nq100: "nasdaq_100", ndx: "nasdaq_100",
  nasdaq: "nasdaq", nascomp: "nasdaq", nq: "nasdaq",
  // Dow + NYSE
  dow_jones: "dow_jones", dow: "dow_jones", dj: "dow_jones",
  nyse: "nyse", ny: "nyse",
  // AMEX stocks (the all-issues list is `amex_all`, lib/indices/generated.ts)
  amex: "amex",
  // Base, the cap bands, ETFs (lib/indices/screenerRules.ts)
  base: "base",
  mega_cap: "mega_cap", large_cap: "large_cap", mid_cap: "mid_cap", small_cap: "small_cap", micro_cap: "micro_cap",
  etfs: "etfs",
};

/** Resolve any historical alias / spelling / breadth code to the canonical slug,
 *  or null if it isn't a known index list (`all` / `everything` are NOT lists —
 *  they're the whole active universe and are handled by their own callers). */
export function canonicalizeSlug(raw: string): IndexSlug | null {
  if (!raw) return null;
  const k = raw.trim().toLowerCase();
  return ALIASES[k] ?? null;
}

/** True when `raw` resolves to a known index list. */
export function isIndexSlug(raw: string): boolean {
  return canonicalizeSlug(raw) !== null;
}
